import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import ReferCandidateCard from './ReferCandidateCard';
import { getReferrals } from '../services/apiService';
// Removed useMultilingualText to fix infinite loop issue
import FullPageLoader from './FullPageLoader';

// Deduplicate in-flight referrals fetch across StrictMode double-invocations
// This ensures only one network call is made per screen load; subsequent
// concurrent invocations will await the same promise.
let referralsFetchInFlight = null;

const StatCard = ({ value, label, color }) => (
    <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', border: '1px solid #e0e7ff' }}>
        <div style={{ fontSize: '2rem', fontWeight: '700', color: color, marginBottom: '0.5rem' }}>{value}</div>
        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '500' }}>{label}</div>
    </div>
);

const ReferralCard = ({ referral, onViewProgress }) => {
    // Color-coding based on backend status while displaying raw text
    const getStatusColors = (statusRaw) => {
        const s = (statusRaw || '').toUpperCase().trim();
        // Categories
        const positive = new Set(['APPROVED', 'JOINED', 'SELECTED']);
        const inProgress = new Set(['IN PROCESS', 'ASSIGNED', 'SCHEDULED', 'OFFERED', 'SELECTED - INTERVIEW 1']);
        const pending = new Set(['SIGN IN PENDING']);
        const negative = new Set(['REJECTED', 'NOT JOINED', 'REMOVED']);
        const neutral = new Set(['POSITION CLOSED', 'INTERNAL TRANSFER', 'EXITED']);

        if (positive.has(s)) return { border: '#10b981', bg: '#dcfce7', text: '#16a34a' }; // green
        if (inProgress.has(s)) return { border: '#2563eb', bg: '#dbeafe', text: '#2563eb' }; // blue
        if (pending.has(s)) return { border: '#f59e0b', bg: '#fef3c7', text: '#d97706' }; // amber
        if (negative.has(s)) return { border: '#ef4444', bg: '#fef2f2', text: '#ef4444' }; // red
        if (neutral.has(s)) return { border: '#6b7280', bg: '#f3f4f6', text: '#6b7280' }; // gray
        // default
        return { border: '#e0e7ff', bg: '#f3f4f6', text: '#374151' };
    };

    const { bg: bgColor, text: textColor } = getStatusColors(referral.status);
    const icon = 'fa-tag';

    return (
        <div className="job-card enhanced-application-card" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)',
            border: '1px solid #e0e7ff'
        }}>
            <div style={{ marginBottom: '1rem' }}>
                {/* Top Row - Name and Status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <h3 style={{
                        margin: 0,
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1f2937'
                    }}>{referral.name}</h3>
                    <div style={{
                        background: bgColor,
                        color: textColor,
                        padding: '0.5rem 1rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <i className={`fas ${icon}`}></i>
                        {referral.status || 'Pending'}
                    </div>
                </div>
                
                {/* Candidate Details */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#6b7280'
                }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fas fa-phone" style={{ color: '#2563eb', width: '12px' }}></i> 
                        {referral.mobile || referral.phone || 'No Phone'}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="fas fa-briefcase" style={{ color: '#f59e0b', width: '12px' }}></i> 
                        {referral.applications} jobs applied
                    </span>
                </div>
            </div>
            
        </div>
    );
};

const Referrals = () => {
    const { isAuthenticated, currentUser, showSignIn, loading: authLoading } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [stats, setStats] = useState({
        referrals: 0,
        applies: 0,
        hires: 0,
        completions: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // removed isRefreshing UI state since Refresh button is gone
    
    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            showSignIn();
        }
    }, [authLoading, isAuthenticated, showSignIn]);

    // Fetch referrals data from API
    const fetchReferralsData = useCallback(async () => {
        console.log('[Referrals] fetchReferralsData called, user:', currentUser);
        console.log('[Referrals] isAuthenticated:', isAuthenticated);
        
        if (!isAuthenticated) {
            console.log('[Referrals] Not authenticated, skipping API call');
            setLoading(false);
            return;
        }
        
        if (!currentUser?.mobileNumber) {
            console.log('[Referrals] No mobile number found, skipping API call');
            setLoading(false);
            return;
        }
        
        console.log('[Referrals] User mobile number found:', currentUser.mobileNumber, 'calling getReferrals API');
        
        try {
            setError(null);
            setLoading(true);
            console.log('[Referrals] Starting API call to getReferrals...');
            if (!referralsFetchInFlight) {
                referralsFetchInFlight = getReferrals();
            } else {
                console.log('[Referrals] Reusing in-flight referrals request');
            }
            const data = await referralsFetchInFlight;
            
            console.log('[Referrals] API call completed. Fetched data:', data);
            console.log('[Referrals] Raw referrals array:', data?.referrals);
            console.log('[Referrals] Referrals count:', data?.referrals?.length || 0);
            
            // Ensure we have valid data structure
            if (!data || typeof data !== 'object') {
                throw new Error('Invalid API response format');
            }
            
            // Handle empty or invalid referrals array
            const referralsArray = Array.isArray(data.referrals) ? data.referrals : [];
            
            // Map API data to component format
            const mappedReferrals = referralsArray.map((referral, index) => ({
                id: referral?.id || `referral-${index}`,
                name: referral?.name || 'No Name',
                // Show the exact status from API (no UI-side mapping)
                status: referral?.status ?? 'Pending',
                mobile: referral?.mobile || referral?.mobile_number || '',
                phone: referral?.mobile || referral?.mobile_number || '', // Keep both for compatibility
                referredDate: formatDate(referral?.referredDate),
                applications: referral?.applicationCount || 0,
                email: referral?.email || '',
                jobTitle: referral?.jobTitle || 'Direct Referral',
                reward: referral?.reward || 0
            }));
            
            console.log('[Referrals] Mapped referrals:', mappedReferrals);
            console.log('[Referrals] Setting referrals state with', mappedReferrals.length, 'items');
            
            setReferrals(mappedReferrals);
            
            // Handle stats with fallback values
            const statsData = data.stats || {};
            setStats({
                referrals: statsData.referrals || mappedReferrals.length || 0,
                applies: statsData.applies || 0,
                hires: statsData.hires || 0,
                completions: statsData.completions || 0
            });
            
        } catch (err) {
            console.error('[Referrals] Error fetching referrals:', err);
            setError(`Failed to load referrals: ${err.message || 'Please try again.'}`);
        } finally {
            referralsFetchInFlight = null;
            setLoading(false);
        }
    }, [isAuthenticated, currentUser]);
    
    // Removed UI-side status mapping to show raw API statuses on cards
    
    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateString;
        }
    };
    
    // Refresh referrals data
    const refreshReferrals = async () => {
        setLoading(true);
        await fetchReferralsData();
    };
    
    // Load data on component mount and when user changes
    useEffect(() => {
        let isMounted = true;
        
        console.log('[Referrals] Component mounted or dependencies changed');
        console.log('[Referrals] isAuthenticated:', isAuthenticated);
        console.log('[Referrals] currentUser:', currentUser);
        console.log('[Referrals] currentUser?.mobileNumber:', currentUser?.mobileNumber);
        
        const loadData = async () => {
            if (isAuthenticated && currentUser?.mobileNumber) {
                console.log('[Referrals] Conditions met, calling fetchReferralsData');
                if (isMounted) {
                    await fetchReferralsData();
                }
            } else {
                // If user is authenticated but user details aren't ready yet, keep skeleton visible
                // If not authenticated, hide skeleton (Sign-In flow will handle UX)
                const shouldShowSkeleton = isAuthenticated && !currentUser?.mobileNumber;
                console.log('[Referrals] Conditions not met. isAuthenticated:', isAuthenticated, 'hasMobile:', !!currentUser?.mobileNumber, 'keeping skeleton:', shouldShowSkeleton);
                if (isMounted) {
                    setLoading(!shouldShowSkeleton ? false : true);
                }
            }
        };
        
        loadData();
        
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, currentUser, fetchReferralsData]);

    // Compute dynamic status options and filtered list based on available data
    const statusOptions = useMemo(() => {
        const counts = referrals.reduce((acc, r) => {
            const s = r.status || 'Pending';
            acc[s] = (acc[s] || 0) + 1;
            return acc;
        }, {});
        const entries = Object.entries(counts)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([status, count]) => ({ value: status, label: `${status} (${count})` }));
        return [{ value: 'All', label: `All Referrals (${referrals.length})` }, ...entries];
    }, [referrals]);

    const filteredReferrals = useMemo(() => {
        if (selectedStatus === 'All') return referrals;
        return referrals.filter(r => r.status === selectedStatus);
    }, [referrals, selectedStatus]);

    // While auth is initializing, show a neutral full-page loader
    if (authLoading) {
        return <FullPageLoader text="Loading..." />;
    }

    const generateTimeline = (status) => {
        const baseTimeline = [
            { name: 'Referred', description: 'Candidate referred successfully', icon: 'fa-user-plus', completed: true, time: '2 days ago' },
            { name: 'Applied', description: 'Candidate applied for positions', icon: 'fa-paper-plane', completed: status !== 'Pending', time: status !== 'Pending' ? '1 day ago' : null },
            { name: 'Interview', description: 'Interview scheduled', icon: 'fa-video', completed: status === 'Interview' || status === 'Hired', time: status === 'Interview' || status === 'Hired' ? '6 hours ago' : null },
            { name: 'Hired', description: 'Successfully hired', icon: 'fa-check-circle', completed: status === 'Hired', time: status === 'Hired' ? '2 hours ago' : null }
        ];
        return baseTimeline;
    };

    const handleViewProgress = (referral) => {
        setSelectedReferral(referral);
        setTimeline(generateTimeline(referral.status));
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedReferral(null);
    };

    return (
        <>
            {/* Blue Theme Header */}
            <section style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)',
                color: 'white',
                padding: windowWidth <= 480 ? '1.5rem 0' : windowWidth <= 768 ? '2rem 16px' : '2.5rem 24px',
                position: 'relative',
                overflow: 'hidden',
                boxSizing: 'border-box'
            }}>
                <div style={{ position: 'relative', zIndex: 1, margin: windowWidth <= 480 ? '0 16px' : '0', boxSizing: 'border-box', width: windowWidth <= 480 ? 'calc(100% - 32px)' : '100%', maxWidth: '100%' }}>
                    <div style={{ textAlign: 'left', width: '100%', maxWidth: '100%' }}>
                        <h1 style={{
                            fontSize: windowWidth <= 480 ? '0.9rem' : windowWidth <= 768 ? '1.75rem' : '2.25rem',
                            marginBottom: '0.5rem',
                            fontWeight: '700',
                            lineHeight: '1.1',
                            margin: '0 0 0.5rem 0',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            boxSizing: 'border-box'
                        }}>
                            <i className="fas fa-user-friends" style={{ color: 'rgba(255,255,255,0.9)', fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '1.5rem' : '2.25rem', marginRight: windowWidth <= 480 ? '8px' : '0.5rem' }}></i>My Referrals
                        </h1>
                        <p style={{
                            fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '0.875rem' : '1rem',
                            opacity: 0.9,
                            fontWeight: 400,
                            margin: 0,
                            lineHeight: '1.4'
                        }}>
                            Refer candidates and earn rewards for successful placements
                        </p>
                    </div>
                </div>
            </section>

            {/* Blue Theme Statistics */}
            <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                padding: '2rem 24px',
                borderBottom: '1px solid #e0e7ff'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: window.innerWidth <= 768 ? '1rem' : '2rem',
                    textAlign: 'center'
                }}>
                    <StatCard value={loading ? '-' : stats.referrals} label="Total Referrals" color="#2563eb" />
                    <StatCard value={loading ? '-' : stats.applies} label="Applications Submitted" color="#3b82f6" />
                    <StatCard value={loading ? '-' : stats.hires} label="Candidates Joined" color="#1d4ed8" />
                    <StatCard value={loading ? '-' : stats.completions} label="Completed One Month" color="#6366f1" />
                </div>
            </div>

            {/* Main Content */}
            <div style={{
                background: '#f8fafc',
                padding: '24px',
                minHeight: '60vh'
            }}>
                <div className="main-content" style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '250px 1fr 280px',
                    gap: '24px',
                    maxWidth: '100%',
                    margin: '0 auto',
                    padding: '0'
                }}>
                        {/* Left Sidebar - Simple Filters */}
                        <aside className="left-sidebar">
                            <div className="sidebar-section">
                                <h3>Filter Referrals</h3>
                                <div className="filter-group">
                                    <label>Status</label>
                                    <select
                                        className="filter-input"
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                    >
                                        {statusOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Date Referred</label>
                                    <select className="filter-input">
                                        <option>All Time</option>
                                        <option>Last 30 days</option>
                                        <option>Last 3 months</option>
                                        <option>Last 6 months</option>
                                    </select>
                                </div>
                            </div>
                        </aside>

                        {/* Main Referrals List */}
                        <main className="job-listings" style={{
                            background: 'white',
                            padding: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            
                            {/* Loading State */}
                            {loading && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    {[...Array(3)].map((_, index) => (
                                        <div key={index} style={{
                                            background: 'white',
                                            borderRadius: '12px',
                                            padding: '1.5rem',
                                            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)',
                                            border: '1px solid #e0e7ff',
                                            animation: 'pulse 2s infinite'
                                        }}>
                                            <div style={{ height: '20px', background: '#f3f4f6', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
                                            <div style={{ height: '16px', background: '#f3f4f6', borderRadius: '4px', width: '60%' }}></div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Error State */}
                            {error && (
                                <div style={{
                                    background: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: '12px',
                                    padding: '1.5rem',
                                    textAlign: 'center'
                                }}>
                                    <i className="fas fa-exclamation-triangle" style={{ color: '#ef4444', fontSize: '2rem', marginBottom: '1rem' }}></i>
                                    <h3 style={{ color: '#dc2626', margin: '0 0 0.5rem 0' }}>Error Loading Referrals</h3>
                                    <p style={{ color: '#7f1d1d', margin: '0 0 1rem 0' }}>{error}</p>
                                    <button
                                        onClick={refreshReferrals}
                                        style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '0.5rem 1rem',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        Try Again
                                    </button>
                                </div>
                            )}
                            
                            {/* Empty State */}
                            {!loading && !error && referrals.length === 0 && (
                                <div style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '3rem 1.5rem',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)',
                                    border: '1px solid #e0e7ff'
                                }}>
                                    <i className="fas fa-user-friends" style={{ color: '#6b7280', fontSize: '3rem', marginBottom: '1rem' }}></i>
                                    <h3 style={{ color: '#1f2937', margin: '0 0 0.5rem 0' }}>No Referrals Yet</h3>
                                    <p style={{ color: '#6b7280', margin: '0 0 1.5rem 0' }}>Start referring candidates to earn rewards!</p>
                                    <button
                                        style={{
                                            background: '#2563eb',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.75rem 1.5rem',
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Refer Your First Candidate
                                    </button>
                                </div>
                            )}
                            
                            {/* Referrals List */}
            {console.log('[Referrals] Render - loading:', loading, 'error:', error, 'referrals.length:', referrals.length)}
            {!loading && !error && referrals.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                    {filteredReferrals.map((referral, index) => (
                                        <ReferralCard key={referral.id || index} referral={referral} onViewProgress={handleViewProgress} />
                                    ))}
                                </div>
                            )}
                        </main>

                    {/* Right Sidebar */}
                    <aside className="right-sidebar">
                        <ReferCandidateCard onReferralSuccess={refreshReferrals} />
                    </aside>
                </div>
            </div>

            {isModalOpen && selectedReferral && (
                <div className={`modal-overlay ${isModalOpen ? 'active' : ''}`}>
                    <div className="modal-content">
                        <button className="modal-close" onClick={closeModal}><i className="fas fa-times"></i></button>
                        <h2 style={{ marginTop: 0, color: '#1f2937', fontSize: '1.75rem', fontWeight: 700 }}>Referral Progress: {selectedReferral.name}</h2>
                        <p style={{ color: '#64748b', marginBottom: '2rem' }}>Track the status of your referral from application to hiring.</p>
                        <div className="progress-timeline">
                            {timeline.map((item, index) => (
                                <div key={index} className={`timeline-item ${item.completed ? 'completed' : ''}`}>
                                    <div className="timeline-icon"><i className={`fas ${item.icon}`}></i></div>
                                    <div className="timeline-content">
                                        <h4>{item.name}</h4>
                                        <p>{item.description}</p>
                                        {item.completed && <span className="time">{item.time}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
};

export default Referrals;
